<template>
  <div class="models-page">
    <div class="page-header flex space-between align-center mb-3">
      <h1 class="text-display ml-4">{{ mixerName || 'Міксер' }}</h1>
      <div>
        <v-btn class="ml-4" medium @click="showAddModel = true" :disabled="isLoadingModels">
          <v-icon position-left font-size="1" icon="plus"></v-icon>
          <span>Додати модель</span>
        </v-btn>
      </div>
    </div>

    <div v-if="isLoadingModels" class="loading-container empty-state">
      <span>Завантаження моделей...</span>
    </div>

    <div v-else-if="modelsError" class="error-state">
      <p>Помилка завантаження: {{ modelsError.message }}</p>
      <v-btn @click="loadData">Спробувати знову</v-btn>
    </div>

    <div v-else-if="modelsCount === 0" class="empty-state">
      <p>Немає моделей для цього міксера</p>
    </div>

    <div v-else class="models-grid list">
      <v-card v-for="model in allModels" :key="model.id" class="model-card v-card">
        <div class="model-header flex space-between align-center">
          <h2 class="model-name card-title" @click="goToCalibrations(model.id)">{{ model.name }}</h2>
          <v-btn class="v-btn--icon v-btn--default" medium @click="editModel(model)">
            <v-icon font-size="1.3" icon="cog"></v-icon>
          </v-btn>
        </div>
        <div class="model-calibrations">
        </div>
      </v-card>
    </div>

    <!-- Модалки -->
    <v-dialog max-width="330" v-model="showAddModel">
      <AddModelModal @close="showAddModel = false" @create="handleAddModel" />
    </v-dialog>

    <v-dialog max-width="330" v-model="showEditModel">
      <EditModelModal :selected="selectedModel" @close="showEditModel = false" 
        @update="handleModelUpdated" @delete="handleModelDeleted" />
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import AddModelModal from '../modals/AddModelModal.vue'
import EditModelModal from '../modals/EditModelModal.vue'

export default {
  name: 'ModelsView',
  components: { AddModelModal, EditModelModal },

  props: {
    mixerId: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      showAddModel: false,
      showEditModel: false,
      selectedModel: null
    }
  },

  computed: {
    ...mapState('baseMixers', ['currentMixer']),
    ...mapGetters('baseMixers', [
      'allModels',
      'mixerName',
      'modelsCount',
      'isLoadingModels',
      'modelsError'
    ])
  },

  async mounted () {
    await this.loadData()
  },

  methods: {
    ...mapActions('baseMixers', [
      'loadMixer',
      'loadModels',
      'createModel',
      'updateModel',
      'deleteModel',
      'clearModels'
    ]),

    async loadData () {
      try {
        this.clearModels()

        await Promise.all([
          this.loadMixer(this.mixerId),
          this.loadModels(this.mixerId)
        ])
      } catch (error) {
        console.error('Error loading data:', error)
        this.$toasted.error('Не вдалося завантажити дані')
      }
    },

    // Перехід до калібрувань моделі
    goToCalibrations (modelId) {
      this.$router.push(`/app/mixer-db/${this.mixerId}/models/${modelId}/calibrations`)
    },

    editModel (model) {
      this.selectedModel = { ...model }
      this.showEditModel = true
    },

    async handleAddModel (modelData) {
      try {
        const payload = {
          ...modelData,
          brand: +this.mixerId
        }

        await this.createModel(payload)

        this.showAddModel = false
        this.$toasted.success('Модель успішно додана')
      } catch (error) {
        console.error('Error creating model:', error)
        this.$toasted.error('Не вдалося створити модель')
      }
    },

    async handleModelUpdated (updatedModelData) {
      try {
        const result = await this.updateModel({
          id: updatedModelData.id,
          data: updatedModelData
        })
        this.$store.commit('baseMixers/SET_CURRENT_MODEL', result)
        this.showEditModel = false
        this.$toasted.success('Модель успішно оновлена')
      } catch (error) {
        console.error('Error updating model:', error)
        this.$toasted.error('Не вдалося оновити модель')
      }
    },

    async handleModelDeleted (modelId) {
      if (!modelId) {
        console.error('No model ID received from modal')
        this.$toasted.error('Немає ID для видалення моделі')
        return
      }

      try {
        await this.deleteModel(modelId)
        this.showEditModel = false
        this.$toasted.success('Модель успішно видалена')
      } catch (error) {
        console.error('Error deleting model:', error)
        this.$toasted.error('Не вдалося видалити модель')
      }
    }
  },
  watch: {
    mixerId: {
      handler: async function (newMixerId, oldMixerId) {
        if (newMixerId && newMixerId !== oldMixerId) {
          this.clearModels()
          await this.loadData()
        }
      },
      immediate: false
    }
  },

  beforeDestroy () {
    this.clearModels()
  }
}
</script>

<style lang="scss" scoped>
  @import '@/sass/abstracts/_mixins.scss';
.models-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  column-gap: 1.5rem;
  row-gap: 1.5rem;
    @include tablet {
       grid-template-columns: 1fr 1fr;
    }
    @include mobile {
       grid-template-columns: 1fr;
    }
}

.model-card {
  padding: 1.5rem 0.5rem 1.5rem 0;
  min-height: 8rem;
  display: flex;
  align-items: center;
}

.model-header {
  width: 100%;
}

.model-name {
  padding-right: .5rem;
  padding-left: 2.5rem;
  border-left: .4rem solid #1da643;
  min-height: 4.3rem;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 1rem;
}

.empty-state {
  margin-top: 3.4rem;
  text-align: center;
  padding: 2rem;
  color: #666;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.error-state {
  text-align: center;
  padding: 2rem;
  color: #d32f2f;
}
</style>